// Central API client for Gurukul Enterprise backend

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export interface ApiResponse<T = unknown> {
    success: boolean;
    data: T;
    message: string;
}

export interface ApiError {
    success: false;
    message: string;
    errors?: Record<string, string[]>;
}

/**
 * Generic API request helper.
 * Handles JSON serialization, auth headers, and error unwrapping.
 */
export async function apiRequest<T = unknown>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    const token = localStorage.getItem('auth_token');

    const method = (options.method || 'GET').toUpperCase();
    const isFormData = options.body instanceof FormData;
    // GET/HEAD/DELETE carry no body. Sending Content-Type on a bodyless request
    // trips ModSecurity in production (406), even though the header is harmless
    // to the app. Only advertise a body type when there's actually a body.
    const isBodylessMethod = method === 'GET' || method === 'HEAD' || method === 'DELETE';

    const headers: Record<string, string> = {
        'Accept': 'application/json',
        // Skip Content-Type for FormData (browser sets the multipart boundary)
        // and for bodyless methods (avoids the ModSecurity 406 above).
        ...(isFormData || isBodylessMethod ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(options.headers as Record<string, string> || {}),
    };

    // Guard against a caller merging Content-Type back in for a bodyless request.
    if (isBodylessMethod) {
        delete headers['Content-Type'];
        delete headers['content-type'];
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
    });

    // Session expired or invalid token — clear storage and redirect to login
    if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('organization_id');
        localStorage.removeItem('school_id');
        window.location.href = '/login';
        throw { success: false, message: 'Session expired. Please log in again.' } as ApiError;
    }

    const json = await response.json();

    if (!response.ok || !json.success) {
        const error: ApiError = {
            success: false,
            message: json.message || `Request failed with status ${response.status}`,
            errors: json.errors,
        };
        throw error;
    }

    return json as ApiResponse<T>;
}
